import { defineComponent as _defineComponent } from 'vue'
import type { ProfileType } from '@/@types/graphql-types';
import type { SnowplowContext } from '@/helpers/tracking/providers';
import type { ProfileInfoFragment } from '@/features/user-profile/graphql/fragments/ProfileInfo.fragment';
import type { TextRecommendationFragment } from '@/features/why-to-watch/graphql/fragments/TextRecommendation.fragment';

import PosterGraphql from '@/components/poster/PosterGraphql.vue';
import Popover from '@/ui-components/Popover.vue';
import ProfileBox from '@/features/user-profile/components/ProfileBox.vue';
import TitleDetailLink from '@/components/nav/links/TitleDetailLink.vue';
import TitleDetailTitleClipsV1 from '@/pages/titleDetailV1/v1/TitleDetailTitleClipsV1.vue';
import TextRecommendationCardModal from '@/features/why-to-watch/components/TextRecommendationCardModal.vue';

import { useLanguageStore, useUserStore } from '@/helpers/composables/useStores';
import { useTextRecommendation, getTextRecAnchor } from '@/helpers/composables/useTextRecommendation';
import { toastError, toastSuccess } from '@/helpers/toast/toast-helper';

import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { ShareMethod } from '@/enums/share-methods';

import { computed, onMounted, ref } from 'vue';
import { faPencil, faEllipsis, faTrash } from '@fortawesome/free-solid-svg-icons';
import { faCopy } from '@fortawesome/free-regular-svg-icons';
import { faFacebook } from '@fortawesome/free-brands-svg-icons';
import { TitleDetail } from '@/interfaces/title-details-graphql';
import { useRoute } from 'vue-router/composables';
import { disableScroll, enableScroll } from '@/helpers/scroll-helper';
import TextRecommendationModal from './TextRecommendationModal.vue';

import { useMutation } from '@/helpers/composables/useApollo';
import {
	DeleteTextRecommendationDocument,
	DeleteTextRecommendationMutation,
	DeleteTextRecommendationMutationVariables,
} from '@/features/why-to-watch/graphql/mutations/DeleteTextRecommendation.mutation';


export default /*#__PURE__*/_defineComponent({
  __name: 'TextRecommendationCard',
  props: {
    textRecommendation: null,
    title: null,
    isSingleTextRecommendation: { type: Boolean, default: false },
    hideMedia: { type: Boolean, default: false },
    profile: null,
    hideBadge: { default: () => [] },
    hideAuthor: { type: Boolean },
    moduleGroup: { default: '' },
    additionalContexts: { default: () => [] }
  },
  emits: ["posterClick"],
  setup(__props: any) {

const props = __props as { textRecommendation: TextRecommendationFragment, title?: TitleDetail, isSingleTextRecommendation: boolean, hideMedia: boolean, profile?: ProfileInfoFragment, hideBadge: ProfileType[], hideAuthor?: boolean, moduleGroup: string, additionalContexts: SnowplowContext[] };





const userStore = useUserStore();
const { language } = useLanguageStore();

const textRecommendationTitle = computed(() => props.textRecommendation.title ?? props.title);

const {
	hasTags,
	profile,
	textRecTitle,
	// likedByUser,
	// likedCount,
	shareId,
	tagsString,
	watchedOnText,
	trackShareClicked,
	shareRecommendation,
	// likeRecommendationMutation,
	textRecommendationHasVideo,
} = useTextRecommendation({
	textRecommendation: () => props.textRecommendation,
	title: () => textRecommendationTitle.value ?? null,
});

const route = useRoute();
const fromRecShare = ref(false);
const displayCardModal = ref(false);
const displayTextRecommendationModal = ref(false);
const showOriginalText = ref<boolean>(false);
const activeClipId = ref<string | null>(null);

// On pages like UserProfile, profiles aren't fetched with the text recommendation but passed as a prop
const actualProfile = computed(() => props.profile ?? profile.value);

const headline = computed(() => props.textRecommendation.headline);
const originalHeadline = computed(() => props.textRecommendation.originalHeadline);
const body = computed(() => props.textRecommendation.body);
const bodyHTML = computed(() => props.textRecommendation.bodyHTML);
const originalBodyHTML = computed(() => props.textRecommendation.originalBodyHTML);

const displayedHeadline = computed(() => {
	if (showOriginalText.value && originalHeadline.value) return originalHeadline.value;
	return headline.value;
});

const displayedBodyHTML = computed(() => {
	if (showOriginalText.value && originalBodyHTML.value) return originalBodyHTML.value;
	return bodyHTML.value;
});

const shareIcon = `/${ASSETS_DIR}/img/share.svg`;

const isTitleDetail = computed(() => {
	return route.name?.includes('app.detail');
});

const showUserOpts = computed(() => props.textRecommendation.ownedByUser);

const anchorFullPath = computed(() => `${textRecTitle.value?.content.fullPath}#${getTextRecAnchor(actualProfile)}`);

const backgroundImageUrl = computed(
	() =>
		`${JW_CONFIG.IMAGESCALER_URL}${textRecTitle.value?.content.posterUrl
			?.replace('{profile}', 's166')
			.replace('{format}', 'avif')}`
);

const isTranslated = computed(() => {
	return !!(props.textRecommendation.originalHeadline || props.textRecommendation.originalBody);
});

const componentContainer = computed(() => {
	if (!isTitleDetail.value) {
		return {
			cmp: TitleDetailLink,
			props: {
				title: textRecTitle.value,
				fullPath: anchorFullPath.value,
			},
		};
	} else {
		return {
			cmp: 'div',
		};
	}
});

const displayReadMore = computed(() => {
	if (props.isSingleTextRecommendation || !isTitleDetail.value) return false;

	const bodyLength = body.value?.length ?? 0;

	if (hasTags.value) {
		return bodyLength > 505;
	} else {
		return bodyLength > 560;
	}
});

function handleShowTranslation() {
	showOriginalText.value = !showOriginalText.value;
}

// function handleLikeClick() {
// 	const likeValue = !likedByUser.value;

// 	likeRecommendationMutation({
// 		input: {
// 			uuid: props.textRecommendation.id,
// 			like: likeValue,
// 		},
// 		country: country.value,
// 		language: language.value,
// 	});

// 	TrackingHelper.trackEvent(
// 		'userinteraction',
// 		{
// 			action: 'helpful_clicked',
// 			value: likeValue ? 1 : 0,
// 		},
// 		props.additionalContexts
// 	);
// }

function toggleCardModal(display: boolean) {
	displayCardModal.value = display;

	if (displayCardModal.value) {
		TrackingHelper.trackEvent(
			'userinteraction',
			{
				action: 'read_more_clicked',
			},
			props.additionalContexts
		);
	}

	displayCardModal.value ? disableScroll() : enableScroll();
}

function toggleTextRecommendationModal(display: boolean) {
	displayTextRecommendationModal.value = display;

	displayTextRecommendationModal.value ? disableScroll() : enableScroll();
}

const deleteRecommendation = useMutation<DeleteTextRecommendationMutation, DeleteTextRecommendationMutationVariables>(
	DeleteTextRecommendationDocument,
	{
		refetchQueries: ['GetTextRecommendations'],
	}
);
function deleteTextRecommendation() {
	deleteRecommendation.mutate({
		input: {
			id: props.textRecommendation.id,
		},
	});
}
deleteRecommendation.onDone(() => {
	toastSuccess({
		message: 'Recommendation deleted',
		duration: 3000,
	});
});
deleteRecommendation.onError(() => {
	toastError({
		message: 'Failed to delete recommendation',
		duration: 3000,
	});
});

onMounted(() => {
	if (route.hash !== `#${shareId.value}`) return;

	// Waiting a tiny bit because anchor links work right away but the page content
	// is still rendering and shifting (on mounted renders like ads, experiments, etc.)
	// This helps improve the experience.
	setTimeout(() => {
		const elements = [...document.querySelectorAll(route.hash)];
		const element = elements.find(e => e != null && e.checkVisibility());
		if (element != null) {
			element?.scrollIntoView();
			fromRecShare.value = true;
		}
	}, 250);
});

return { __sfc: true,props, userStore, language, textRecommendationTitle, hasTags, profile, textRecTitle, shareId, tagsString, watchedOnText, trackShareClicked, shareRecommendation, textRecommendationHasVideo, route, fromRecShare, displayCardModal, displayTextRecommendationModal, showOriginalText, activeClipId, actualProfile, headline, originalHeadline, body, bodyHTML, originalBodyHTML, displayedHeadline, displayedBodyHTML, shareIcon, isTitleDetail, showUserOpts, anchorFullPath, backgroundImageUrl, isTranslated, componentContainer, displayReadMore, handleShowTranslation, toggleCardModal, toggleTextRecommendationModal, deleteRecommendation, deleteTextRecommendation, PosterGraphql, Popover, ProfileBox, TitleDetailTitleClipsV1, TextRecommendationCardModal, ShareMethod, faPencil, faEllipsis, faTrash, faCopy, faFacebook, TextRecommendationModal }
}

})